import { useEffect, useMemo } from "react";
import {
  useForm,
  FormProvider,
  useFormContext,
  useFieldArray,
} from "react-hook-form";
import {
  Box,
  VStack,
  HStack,
  Button,
  IconButton,
  Select,
  FormControl,
  FormLabel,
  Text,
} from "@chakra-ui/react";
import { CloseIcon } from "@saas-ui/react";
import { demographic_labels } from "./demoLabels";

const ArrayFilters = ({ data, id, rank, setRank }) => {
  const { control, register, watch } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filters",
  });
  const filters = watch("filters");

  const multipleChoiceOptionsMap = useMemo(() => {
    return (data || [])
      .filter(
        (q) => q.question_type === "multiple_choice" && Array.isArray(q.data)
      )
      .reduce((acc, q) => {
        acc[q.question] = {
          question: q.question,
          question_id: q.question_id,
          options: q.data.map((opt) => ({
            label: opt.message || opt.label || opt.name,
            value: opt.answer_id,
          })),
        };
        return acc;
      }, {});
  }, [data]);

  const demographicOptionsMap = useMemo(
    () =>
      Object.entries(demographic_labels).reduce((acc, [key, entries]) => {
        acc[key] = {
          label: key[0].toUpperCase() + key.slice(1),
          value: key,
          options: Object.entries(entries).map(([value, label]) => ({
            value,
            label,
          })),
        };
        return acc;
      }, {}),
    []
  );

  // Helper to get options for the current filter
  const getFilterOptions = (type, questionKey) => {
    if (type === "question") {
      return (
        Object.values(multipleChoiceOptionsMap).find(
          (e) => e.question_id === questionKey
        )?.options || []
      );
    }
    return demographicOptionsMap[questionKey]?.options || [];
  };

  return (
    <Box w={"100%"}>
      <HStack justifyContent={"space-between"}>
        <Text>Filter</Text>
        <HStack spacing={2}>
          <Select
            size="sm"
            maxWidth="100%"
            width="auto"
            value={`${rank.type}-${rank.count}`}
            onChange={(e) => {
              const [type, count] = e.target.value.split('-');
              setRank({ type, count: Number(count) });
            }}
          >
            <option value="top-1" selected>Top 1</option>
            <option value="top-2">Top 2</option>
            <option value="bottom-1">Bottom 1</option>
            <option value="bottom-2">Bottom 2</option>
          </Select>
          <Button
            size="sm"
            onClick={() =>
              append({
                type: "question",
                question: "",
                operator: "IS",
                value: "",
              })
            }
          >
            Add Filter
          </Button>
        </HStack>
      </HStack>
      {fields.length === 0 && (
        <Text fontSize="sm" color="gray.500">
          No filters applied press "Add Filter" to add a filter
        </Text>
      )}
      <VStack spacing={4} p="4" alignItems={"flex-start"}>
        {fields.map((field, index) => {
          const current = filters?.[index] || {};
          const type = current.type || "question";
          const question = current.question || "";
          return (
            <HStack
              key={field.id}
              spacing={2}
              justifyContent={"space-between"}
              w="100%"
            >
              <FormControl>
                <FormLabel>Filter Type</FormLabel>
                <Select {...register(`filters.${index}.type`)}>
                  <option value="question">Question</option>
                  <option value="demographic">Demographic</option>
                </Select>
              </FormControl>
              {type === "question" ? (
                <FormControl>
                  <FormLabel>Question</FormLabel>
                  <Select {...register(`filters.${index}.question`)}>
                    <option value="" hidden selected >
                      Select
                    </option>
                    {Object.entries(multipleChoiceOptionsMap).map(
                      ([text, { question_id }]) => (
                        <option key={question_id} value={question_id}>
                          {text}
                        </option>
                      )
                    )}
                  </Select>
                </FormControl>
              ) : (
                <FormControl>
                  <FormLabel>Demographic</FormLabel>
                  <Select {...register(`filters.${index}.question`)}>
                    <option value="" hidden selected >
                      Select
                    </option>
                    {Object.values(demographicOptionsMap).map(
                      ({ label, value }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      )
                    )}
                  </Select>
                </FormControl>
              )}

              {/* <FormControl>
                <FormLabel>Operator</FormLabel>
                <Select {...register(`filters.${index}.operator`)}>
                  {["IS", "IS NOT"].map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </Select>
              </FormControl> */}

              {type === "question" ? (
                <FormControl>
                  <FormLabel>Value</FormLabel>
                  <Select {...register(`filters.${index}.value`)}>
                    <option value="" hidden selected >
                      Select
                    </option>
                    {getFilterOptions("question", question).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <FormControl>
                  <FormLabel>Value</FormLabel>
                  <Select {...register(`filters.${index}.value`)}>
                    <option value="" hidden selected >
                      Select
                    </option>
                    {getFilterOptions("demographic", question).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}

              <IconButton
                aria-label="Remove filter"
                icon={<CloseIcon />}
                size="sm"
                variant={"ghost"}
                onClick={() => remove(index)}
                alignSelf="flex-end"
              />
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
};

export default function SurveyFilter({ submit, isLoading, id, data, rank, setRank }) {
  // load stored filters
  const stored = localStorage.getItem(`surveyFilters-${id}`);
  const defaultFilters = stored ? JSON.parse(stored) : [];

  const methods = useForm({ defaultValues: { filters: defaultFilters } });
  const { handleSubmit, reset, setValue } = methods;

  const onSubmit = async (values) => {
    localStorage.setItem(`surveyFilters-${id}`, JSON.stringify(values.filters));
    await submit({ id, filters: values.filters });
  };

  const onReset = () => {
    localStorage.removeItem(`surveyFilters-${id}`);
    setRank({ type: "top", count: 1 });
    reset({ filters: [] });
    submit({ id, filters: [] });
  };

  useEffect(() => {
    const storedFilters = localStorage.getItem(`surveyFilters-${id}`);
    if (storedFilters) {
      try {
        const parsed = JSON.parse(storedFilters);
        if (Array.isArray(parsed)) {
          parsed.forEach((f, i) => {
            setValue(`filters.${i}.type`, f.type);
            setValue(`filters.${i}.question`, f.question);
            setValue(`filters.${i}.operator`, f.operator);
            setValue(`filters.${i}.value`, f.value);
          });
        }
      } catch (e) {
        console.error("Error parsing stored filters", e);
      }
    }
  }, [id, setValue]);

  return (
    <Box
      mb={4}
      p="4"
      border="1px"
      borderColor="gray.200"
      rounded="md"
      bg="white"
      shadow="sm"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={4}>
            <ArrayFilters data={data} id={id} rank={rank} setRank={setRank} />
          </Box>
          <HStack justifyContent="flex-end" mt={4}>
            <Button variant="ghost" onClick={onReset}>
              Reset
            </Button>
            <Button type="submit" colorScheme="blue" isLoading={isLoading}>
              Search
            </Button>
          </HStack>
        </form>
      </FormProvider>
    </Box>
  );
}
